import { FC, memo, useMemo } from 'react';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Flex } from 'antd';
import {
  CellContext,
  ColumnDef,
  createColumnHelper,
} from '@tanstack/react-table';
import translations from './RolePermissionTable.translations.json';
import {
  TanstackFormTable,
  FormField,
  CreateFormTableRowButton,
  DeleteFormTableRowButton,
  PermissionDto,
  useInfiniteSearchPermissionsQuery,
  createInfiniteSearchSelect,
  getFormTableFieldPath,
  PermissionOptionDto,
} from '@atlas-frontend/sdk';
import { useFormContext, useWatch } from 'react-hook-form';

const { addTranslations, useTranslation } = i18nNsFactory(
  'role-access-group-table',
);
addTranslations(translations);

const columnHelper = createColumnHelper<PermissionDto>();

export const RolePermissionFormTable = () => {
  const { t } = useTranslation();

  const columns = useMemo(() => {
    const columns: ColumnDef<PermissionDto, any>[] = [
      columnHelper.accessor('name', {
        header: t('column.name'),
        cell: PermissionCell,
      }),
      columnHelper.accessor('checkScope', {
        header: t('column.checkScope'),
        cell: (props) => t(props.getValue() ? 'yes' : 'no'),
        enableSorting: false,
        filterFn: 'includesOption',
        meta: {
          width: '20ch',
          className: 'checkScope',
        },
      }),
      columnHelper.display({
        id: 'operations',
        header: () => (
          <Flex justify="center" gap="small">
            <CreateFormTableRowButton autoFocusField="name" />
          </Flex>
        ),
        cell: (props) => (
          <Flex justify="center" gap="small">
            <DeleteFormTableRowButton context={props} />
          </Flex>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        meta: {
          width: 88,
          className: 'sticky-right',
        },
      }),
    ];

    return columns;
  }, [t]);

  const options = useMemo(
    () => ({
      checkScope: [
        { id: true, name: t('yes') },
        { id: false, name: t('no') },
      ],
    }),
    [t],
  );

  return (
    <Flex align="end" gap="middle" vertical>
      <TanstackFormTable
        name="permissions"
        columns={columns}
        options={options}
        sticky
        virtual
        scrollContainerSelector="#detail-layout-main-content"
      />
    </Flex>
  );
};

const PermissionInfiniteSelect = createInfiniteSearchSelect(
  useInfiniteSearchPermissionsQuery,
);

const PermissionCell: FC<CellContext<PermissionDto, any>> = memo((props) => {
  const { setValue, getValues } = useFormContext();
  const isEditable = !props.row.original.id;
  const permissions = useWatch({ name: 'permissions' }) as PermissionDto[];

  const basePath = getFormTableFieldPath(props).slice(0, -1);
  const id = [...basePath, 'id'].join('.');
  const name = [...basePath, 'name'].join('.');
  const checkScope = [...basePath, 'checkScope'].join('.');

  const excludeIds = useMemo(
    () => permissions?.filter((p) => p?.id).map((p) => p.id) ?? [],
    [permissions],
  );

  return (
    <FormField
      name={name}
      size="small"
      preview={{
        render: () => getValues(name),
        previewOnly: !isEditable,
      }}
    >
      <PermissionInfiniteSelect
        size="small"
        name={name}
        autoFocus
        defaultOpen
        searchOptions={{ excludeIds }}
        onChange={(_value, option: PermissionOptionDto) => {
          setValue(id, option.item.id);
          setValue(checkScope, option.item.checkScope);
        }}
      />
    </FormField>
  );
});
